import {
    PHONE_TEST_ID,
    BILLING_ADDRESS_TEST_ID,
    DELIVERY_METHOD_TEST_ID,
    SHIPPING_ADDRESS_TEST_ID,
    PAYMENT_METHOD_TEST_ID,
} from "@features/orderConfirmation/constants/testIds";

import { getNameFromCountryValue, getNameFromValue } from "./getStateNameFromValue";
import { formatDateForLocale } from "./dateutils";
import type { StoreData } from "@features/stores/types";
import { formatCityLine, formatStreet } from "@components/store-locator/utils/formatAddress";
import { Market } from "@config/site/types";

export type Info = {
    name: string;
    container?: string;
    testId?: string;
    keys: string[][];
};

export type OrderInfo = {
    name: string;
    testId: string;
    value: string[];
    locale?: string;
};

export const getInfoByField = (
    field: Info,
    // This function is structure agnostic, so we use any
    values: { [x: string]: any },
    locale: string = ""
): OrderInfo => {
    const { name, keys, container, testId } = field;
    const output: string[] = [];
    const [, localeCountry] = locale.split("-").map((part) => part.toUpperCase());
    keys.forEach((key) => {
        let value = "";
        key.forEach((keyPart) => {
            const keyPartValue = values?.[container]?.[keyPart] || values[keyPart];
            const separator = keyPart === "city" ? "," : "";

            if (keyPart === "region") {
                if (
                    container === "billingAddress" &&
                    values.billingAddress?.country !== localeCountry &&
                    keyPartValue
                ) {
                    // Workaround to get the region on the billing address, otherwise if we are in ES market...
                    // and want to set a Billing addresss in a country with region, wont work.
                    const localeCountry = values.billingAddress.country;

                    value += ` ${getNameFromCountryValue(keyPartValue, localeCountry, locale)}${separator}`;
                } else if (keyPartValue) {
                    const localeCountry = locale;
                    value += ` ${getNameFromValue(keyPartValue, localeCountry)}${separator}`;
                }
            } else if (keyPart === "date" && container === "deliveryAddress" && keyPartValue) {
                const formattedDate = formatDateForLocale(keyPartValue, locale);
                value += ` ${formattedDate}${separator}`;
            } else if (keyPartValue) {
                value += ` ${keyPartValue}${separator}`;
            }
        });
        output.push(value.trim());
    });

    return {
        name,
        testId: testId,
        value: output,
    };
};

export const getStoreLines = (store?: StoreData) => {
    const lines: string[] = [];
    if (!store) {
        return lines;
    }
    if (store.name) {
        lines.push(store.name);
    }
    const streetName = formatStreet(store);
    if (streetName) {
        lines.push(streetName);
    }
    const cityLines = formatCityLine(store);
    if (cityLines) {
        lines.push(cityLines);
    }

    return lines;
};

export const contactDetails = (name: string): Info => ({
    name: name,
    container: "contactDetailsValues",
    keys: [["email"], ["countryCode", "phone"]],
    testId: PHONE_TEST_ID,
});

export const deliveryMethod = (name: string): Info => ({
    name: name,
    container: "deliveryMethod",
    keys: [["name"]],
    testId: DELIVERY_METHOD_TEST_ID,
});

export const shippingAddress = (name: string, country: string): Info => ({
    name: name,
    container: "deliveryAddress",
    keys: [
        ["company"],
        ["firstName", "lastName"],
        ["streetName"],
        ["additionalAddressInfo"],
        ...(country.toLowerCase() === Market.NZ ? [["district"]] : []),
        ["city", "postalCode"],
        ...(country.toLowerCase() !== Market.NZ ? [["region"]] : []),
        ["date"],
        ["timeslot"],
        ["country"],
        ["landmark"],
        ["deliveryInstructions"],
    ],
    testId: SHIPPING_ADDRESS_TEST_ID,
});

export const billingAddress = (name: string, country: string): Info => ({
    name: name,
    container: "billingAddress",
    keys: [
        ["company"],
        ["firstName", "lastName"],
        ["streetName"],
        ["additionalAddressInfo"],
        ...(country.toLowerCase() === Market.NZ ? [["district"]] : []),
        ["city", "postalCode"],
        ...(country.toLowerCase() !== Market.NZ ? [["region"]] : []),
        ["country"],
    ],
    testId: BILLING_ADDRESS_TEST_ID,
});

export const paymentMethod = (name: string): Info => ({
    name: name,
    container: "paymentMethod",
    keys: [["paymentSystemIcon"], ["paymentSystem"], ["paymentReference"], ["paymentDateOfExpiry"]],
    testId: PAYMENT_METHOD_TEST_ID,
});
